import styled from "styled-components";
import {
  ButtonDS,
  hasEmailFormat,
  InputDS,
  LaunchPage,
  OnboardingPage,
  Spacer,
  TextCapitalized,
  Title,
  ToastContainerDS,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { Assets } from "@qivia/ui";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  loginSlice,
  loginPasswordForgottenAsync,
  selectPasswordForgottenErrorMessage,
  selectStatus,
} from "./loginSlice";
import "../../i18n/i18n";

type EmailInput = {
  email: string;
};

export interface CtaProps {
  type: "email" | "emailSent" | "newPassword";
  disabledNext?: boolean;
  onClickNext: () => void;
  onClickPrevious: () => void;
}

const ButtonsNextAndPrevious = (props: CtaProps) => {
  const { t } = useTranslation();
  return (
    <>
      <ButtonDS
        text={t(`passwordForgotten.${props.type}.cta.continue`) || ""}
        onClick={props.onClickNext}
        disabled={!props.disabledNext}
        format={"fill"}
        buttonType={"primary"}
      />
      <ButtonDS
        format={"fill"}
        buttonType={"secondary"}
        text={t(`passwordForgotten.${props.type}.cta.back`) || ""}
        leftIcon={<Assets.LeftArrowSolid />}
        onClick={props.onClickPrevious}
      />
    </>
  );
};

export const PasswordForgotten = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const passwordForgottenErrorMessage = useAppSelector(
    selectPasswordForgottenErrorMessage,
  );
  const loginStatus = useAppSelector(selectStatus);
  const [route, setRoute] = useState<string>("passwordForgotten");

  const [emailInput, setEmailInput] = useState<EmailInput>({
    email: "",
  });
  const [displayError, setDisplayError] = useState<boolean>(false);

  const hasValidEmail = useMemo(() => {
    return emailInput.email !== "" && hasEmailFormat(emailInput.email);
  }, [emailInput.email]);

  const canResetPassword = useMemo(() => {
    return hasValidEmail && passwordForgottenErrorMessage === null;
  }, [hasValidEmail, passwordForgottenErrorMessage]);

  const resetPassword = useCallback(async () => {
    if (!canResetPassword) {
      setDisplayError(true);
      return;
    }
    dispatch(loginSlice.actions.resetPasswordForgottenErrorMessage());
    await dispatch(loginPasswordForgottenAsync({ email: emailInput.email }));
  }, [canResetPassword, dispatch, emailInput.email]);

  useEffect(() => {
    if (loginStatus === "success") {
      setRoute("passwordForgottenResend");
      setDisplayError(false);
      dispatch(loginSlice.actions.resetLoginStatus());
    }
  }, [dispatch, loginStatus, passwordForgottenErrorMessage]);

  const previousPage = useCallback((route: string) => {
    setRoute(route);
  }, []);

  useEffect(() => {
    if (route === "login") {
      navigate("/login");
    }
  }, [route, navigate]);

  return (
    <>
      {route === "passwordForgotten" ? (
        <OnboardingPage
          title={
            <Title
              text={t("passwordForgotten.email.title") || ""}
              size={"large"}
            ></Title>
          }
          form={
            <div>
              <InputDS
                label={t("passwordForgotten.email.input.title")}
                name="email"
                value={emailInput.email}
                placeholder={t("passwordForgotten.email.input.placeholder")}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  dispatch(
                    loginSlice.actions.resetPasswordForgottenErrorMessage(),
                  );
                  setEmailInput({ email: e.target.value });
                }}
                error={
                  displayError && emailInput.email === ""
                    ? t("passwordForgotten.email.error.empty")
                    : displayError && !hasValidEmail
                      ? t("passwordForgotten.email.error.incorrectFormat")
                      : displayError && passwordForgottenErrorMessage
                        ? t(`passwordForgotten.emailSent.managerNotActive`)
                        : undefined
                }
              />
              <ButtonsNextAndPrevious
                type="email"
                onClickNext={() => {
                  if (!canResetPassword) {
                    setDisplayError(true);
                    return;
                  }
                  void resetPassword();
                }}
                disabledNext={canResetPassword}
                onClickPrevious={() => previousPage("login")}
              />
            </div>
          }
          toaster={<ToastContainerDS />}
        />
      ) : route === "passwordForgottenResend" ? (
        <OnboardingPage
          title={
            <Title
              text={t("passwordForgotten.emailSent.title") || ""}
              size={"large"}
            ></Title>
          }
          form={
            <div>
              <StyledEmailSentSubtitle>
                <TextCapitalized>
                  {t("passwordForgotten.emailSent.subtitle")}
                </TextCapitalized>
              </StyledEmailSentSubtitle>
              <Spacer y={1} />
              <TextCapitalized>
                {t("passwordForgotten.emailSent.content")}
              </TextCapitalized>
              <Spacer y={2} />
              <ButtonsNextAndPrevious
                type="emailSent"
                onClickNext={() => void resetPassword()}
                onClickPrevious={() => previousPage("passwordForgotten")}
                disabledNext={true}
              />
            </div>
          }
          toaster={<ToastContainerDS />}
        />
      ) : (
        <LaunchPage />
      )}
    </>
  );
};

const StyledEmailSentSubtitle = styled.div`
  font-weight: 600;
`;

export const StyledBackgroundButton = styled.div`
  display: flex;
  border: solid 0.25rem transparent;
`;
